import { useSelector } from 'react-redux';
import { useMetrics } from 'hooks/metrics/useMetrics';
import { UNUSED_PARAMETER } from 'hooks/metrics/shared/constants';
import { getSubNodes } from 'containers/Menu/selectors/navData.selector';

import {
  MUNDIAL_EVENTS,
  SCROLL_DIRECTIONS,
  INTERACTION_TYPES,
} from './constants';
import { normalizeText } from './utils/normalizeText';
import { buildPagePath } from './utils/buildPagePath';
import { buildMundialDefaults } from './utils/buildMundialDefaults';
import { buildMatchValues } from './utils/buildMatchValues';
import { buildViewItemDefaults } from './utils/buildViewItemDefaults';
import { getEngagementTime } from './utils/engagementTracker';

/**
 * Hook de metricas especifico del modo Mundial 2026.
 * Reutiliza buildMetricsEvent y sendMetricsEvent del useMetrics generico,
 * y agrega encima los defaults del Mundial (hardcodes + "no aplica" fase 1)
 * y los campos especificos de cada evento.
 *
 * Expone:
 *   - trackSelectContent({ pageTitleSegments, pageTitle, contentSubsection, moduloName, interactionType, statusCard, match })
 *   - trackScroll({ pageTitleSegments, contentSubsection, moduloName, scrollPercentage, visibleContentIds })
 */
export const useMundialMetrics = () => {
  const { buildMetricsEvent, sendMetricsEvent } = useMetrics();
  const selectedNode = useSelector(getSubNodes);
  const contentSection = normalizeText(selectedNode?.text) || undefined;

  const buildBasePayload = (eventName, { pageTitleSegments, contentSubsection, moduloName }) => {
    const baseEvent = buildMetricsEvent(eventName);
    const mundialDefaults = buildMundialDefaults({ contentSection });

    return {
      ...baseEvent,
      ...mundialDefaults,
      content_subsection: normalizeText(contentSubsection) || UNUSED_PARAMETER,
      modulo_name: normalizeText(moduloName) || UNUSED_PARAMETER,
      engagement_time_msec: getEngagementTime(),
    };
  };

  /**
   * @param {Object} params
   * @param {string[]} params.pageTitleSegments  Segmentos para construir page_path.
   * @param {string}   [params.pageTitle]        Texto del boton/tab clickeado (page_title).
   *                                             Fallback: ultimo segmento de pageTitleSegments.
   * @param {string}   params.contentSubsection  Subseccion activa.
   * @param {string}   params.moduloName         Modulo activo.
   * @param {string}   params.interactionType    Usar INTERACTION_TYPES.
   * @param {string}   [params.statusCard]       Estado del card cuando aplica.
   * @param {Object}   [params.match]            Objeto de partido — si es accionable,
   *                                             sus valores sobreescriben los defaults.
   */
  const trackSelectContent = ({
    pageTitleSegments = [],
    pageTitle = '',
    contentSubsection = '',
    moduloName = '',
    interactionType = '',
    statusCard = UNUSED_PARAMETER,
    match = null,
  } = {}) => {
    const matchValues = buildMatchValues(match);

    const lastSegment = (pageTitleSegments || []).filter(Boolean).slice(-1)[0] || '';
    const resolvedPageTitle = normalizeText(pageTitle) || normalizeText(lastSegment) || UNUSED_PARAMETER;

    const basePayload = buildBasePayload(MUNDIAL_EVENTS.SELECT_CONTENT, {
      pageTitleSegments,
      contentSubsection,
      moduloName,
    });

    const payload = {
      ...basePayload,
      page_path: buildPagePath(pageTitleSegments),
      page_title: resolvedPageTitle,
      interaction_type: normalizeText(interactionType) || UNUSED_PARAMETER,
      status_card: matchValues?.status_card || normalizeText(statusCard) || UNUSED_PARAMETER,
      ...(matchValues || {}),
    };

    sendMetricsEvent(payload);
  };

  /**
   * @param {Object} params
   * @param {string[]} params.pageTitleSegments
   * @param {string}   params.contentSubsection
   * @param {string}   params.moduloName
   * @param {number}   params.scrollPercentage  25 | 50 | 75 | 100
   * @param {string}   [params.scrollDirection] 'vertical' | 'horizontal' (default 'vertical')
   * @param {string[]|null} [params.visibleContentIds] IDs de partidos visibles (solo Fixtures).
   */
  const trackScroll = ({
    pageTitleSegments = [],
    contentSubsection = '',
    moduloName = '',
    scrollPercentage,
    scrollDirection = SCROLL_DIRECTIONS.VERTICAL,
    visibleContentIds = null,
  } = {}) => {
    const lastSegment = (pageTitleSegments || []).filter(Boolean).slice(-1)[0] || '';
    const resolvedPageTitle = normalizeText(lastSegment) || UNUSED_PARAMETER;

    const visibleContentValue = (() => {
      if (!Array.isArray(visibleContentIds) || visibleContentIds.length === 0) {
        return UNUSED_PARAMETER;
      }
      const cleaned = visibleContentIds
        .map((id) => normalizeText(id))
        .filter(Boolean);
      return cleaned.length ? cleaned.join(', ') : UNUSED_PARAMETER;
    })();

    const basePayload = buildBasePayload(MUNDIAL_EVENTS.SCROLL, {
      pageTitleSegments,
      contentSubsection,
      moduloName,
    });

    const payload = {
      ...basePayload,
      page_path: buildPagePath(pageTitleSegments),
      page_title: resolvedPageTitle,
      interaction_type: 'scroll',
      scroll_percentage: scrollPercentage,
      scroll_direction: scrollDirection,
      visible_content_id: visibleContentValue,
    };

    sendMetricsEvent(payload);
  };

  /**
   * Evento exclusivo de la pestana de Estadisticas.
   *
   * @param {Object} params
   * @param {string} params.categoryLabel       Categoria superior (ej: 'ofensiva').
   * @param {string} params.subCategoryLabel    Subcategoria inferior (ej: 'goles').
   */
  const trackViewItem = ({
    categoryLabel = '',
    subCategoryLabel = '',
  } = {}) => {
    const normalizedCategory = normalizeText(categoryLabel);
    const normalizedSubCategory = normalizeText(subCategoryLabel);

    if (!normalizedCategory || !normalizedSubCategory) return;

    const moduloName = `${normalizedCategory}, ${normalizedSubCategory}`;
    const contentName = `top ${normalizedSubCategory}`;
    const pageTitleSegments = ['estadisticas', normalizedCategory, normalizedSubCategory];

    const basePayload = buildBasePayload(MUNDIAL_EVENTS.VIEW_ITEM, {
      pageTitleSegments,
      contentSubsection: 'estadisticas de juegos',
      moduloName,
    });

    const payload = {
      ...basePayload,
      page_path: buildPagePath(pageTitleSegments),
      page_title: normalizedSubCategory,
      interaction_type: INTERACTION_TYPES.VIEW_FULL_TOP,
      content_name: contentName,
      status_card: UNUSED_PARAMETER,
      ...buildViewItemDefaults(),
    };

    sendMetricsEvent(payload);
  };

  /**
   * Evento de reproducción del player del Mundial.
   *
   * @param {Object} params
   * @param {string[]} params.pageTitleSegments
   * @param {string}   [params.pageTitle]
   * @param {string}   params.contentSubsection
   * @param {string}   params.moduloName
   * @param {string}   params.interactionType        Usar INTERACTION_TYPES.
   * @param {string}   [params.contentId]            ID del contenido.
   * @param {string}   [params.contentAvailability]  Ej: 'por suscripcion'.
   * @param {boolean}  [params.isLive]               true si el partido está en vivo.
   * @param {string}   [params.tournamentRound]       Ej: 'octavos de final'.
   * @param {Object}   [params.match]                Objeto del partido (status, score, venue, etc.).
   */
  const trackContentPlay = ({
    pageTitleSegments = [],
    pageTitle = '',
    contentSubsection = '',
    moduloName = '',
    interactionType = '',
    provider = UNUSED_PARAMETER,
    contentId = UNUSED_PARAMETER,
    contentAvailability = UNUSED_PARAMETER,
    isLive = false,
    tournamentRound = UNUSED_PARAMETER,
    match = null,
  } = {}) => {
    const matchValues = buildMatchValues(match);

    const lastSegment = (pageTitleSegments || []).filter(Boolean).slice(-1)[0] || '';
    const resolvedPageTitle = normalizeText(pageTitle) || normalizeText(lastSegment) || UNUSED_PARAMETER;

    const basePayload = buildBasePayload(MUNDIAL_EVENTS.CONTENT_PLAY, {
      pageTitleSegments,
      contentSubsection,
      moduloName,
    });

    const payload = {
      ...basePayload,
      page_path: buildPagePath(pageTitleSegments),
      page_title: resolvedPageTitle,
      interaction_type: normalizeText(interactionType) || UNUSED_PARAMETER,
      provider: normalizeText(provider) || UNUSED_PARAMETER,
      content_id: contentId,
      content_availability: normalizeText(contentAvailability) || UNUSED_PARAMETER,
      status_card: matchValues?.status_card || UNUSED_PARAMETER,
      is_live: String(isLive),
      tournament_round: normalizeText(tournamentRound) || UNUSED_PARAMETER,
      ...(matchValues || {}),
    };

    sendMetricsEvent(payload);
  };

  return {
    trackSelectContent,
    trackScroll,
    trackViewItem,
    trackContentPlay,
  };
};
